import { useEffect } from "react";
import { Link } from "react-router-dom";
import { teamMembers } from "@/mocks/collections";
import Breadcrumb from "@/components/base/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";

const About = () => {
  useEffect(() => {
    document.title = "关于我们 - 色即是空影视资料站";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "色即是空影视资料站的定位、编辑团队与联系方式：我们整理经典影视作品的剧情介绍、演员档案与专题策展，欢迎勘误与作品推荐。",
      );
    document
      .querySelector('meta[name="keywords"]')
      ?.setAttribute("content", "关于我们,色即是空,影视资料站,影视剧情介绍,影视资料勘误");
  }, []);

  const principles = [
    { title: "可查证", desc: "每一条资料都能追溯到作品署名与公开档案，不做推测式补全。", icon: "ri-shield-check-line" },
    { title: "可阅读", desc: "剧情介绍由编辑撰写，拒绝复制粘贴，注重叙事脉络而非剧透堆叠。", icon: "ri-book-open-line" },
    { title: "可延伸", desc: "用专题、相关推荐与标签把单部作品放回它所属的时代与母题里。", icon: "ri-node-tree" },
  ];

  return (
    <div className="w-full bg-background-50">
      <section className="relative w-full overflow-hidden border-b border-background-200 bg-foreground-950">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20moody%20archive%20room%20with%20tall%20wooden%20shelves%20of%20film%20canisters%2C%20a%20single%20desk%20lamp%20casting%20amber%20light%2C%20deep%20indigo%20shadows%2C%20dust%20in%20the%20air%2C%20cinematic%20editorial%20photography&width=1600&height=700&seq=sjs-about-hero&orientation=landscape"
            alt="影视资料档案室"
            title="色即是空 影视资料档案室"
            className="h-full w-full object-cover object-top opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/75 to-foreground-950/40" />
        </div>
        <div className="relative mx-auto w-full max-w-[1280px] px-4 pb-12 pt-28 md:px-8 md:pb-16 md:pt-36">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <p className="mt-5 flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-accent-400">
            <span className="h-[2px] w-6 bg-accent-500" />
            About the archive
          </p>
          <h1 className="mt-3 max-w-3xl font-heading text-[30px] font-bold leading-tight text-background-50 md:text-[46px]">
            我们只是把一部电影，讲得更仔细一点
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-[1.9] text-background-200/85 md:text-base">
            色即是空是一个非商业的影视资料整理站点。我们收录经典影视作品的基本档案、剧情脉络、
            演员与主创信息，并在此基础上做主题策展，让喜欢老片的观众有一个可以安静查资料的地方。
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-4 py-12 md:px-8 md:py-16">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">站点定位</h2>
            <div className="mt-4 space-y-4 text-[15px] leading-[1.95] text-foreground-700">
              <p>
                很多影视资料站以「快」为目标：快速给出评分、快速放出资源。我们把顺序反过来，先弄清楚一部片子
                讲了什么、由谁完成、它和哪些作品构成对话，再谈推荐。所以这里没有播放器，也没有下载链接，
                只有可以慢慢读的资料。
              </p>
              <p>
                「色即是空」既是本站的名字，也是我们的起点。这部 2002 年的韩国校园喜剧曾以夸张的笑料被记住，
                但它真正留下的，是那个在难堪里仍然选择认真喜欢别人的年轻人。从它出发，我们收录了同期、
                同母题与同作者脉络的作品，希望你看完一部后，还能找到下一部。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {principles.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200/80 bg-background-100/60 p-4"
                >
                  <span className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  <h3 className="text-sm font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>

            <h2 className="mt-10 font-heading text-2xl font-bold text-foreground-950">编辑团队</h2>
            <p className="mt-2 text-sm text-foreground-500">
              一个小团队，各自负责资料的不同层面，所有条目上线前会经过二次核校。
            </p>
            <div className="mt-5 space-y-3">
              {teamMembers.map((member, index) => (
                <div
                  key={member.name}
                  className="flex gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4"
                >
                  <span
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full font-heading text-lg font-semibold ${
                      index === 0
                        ? "bg-primary-100 text-primary-700"
                        : index === 1
                          ? "bg-accent-100 text-accent-800"
                          : "bg-secondary-100 text-secondary-800"
                    }`}
                  >
                    {member.name.slice(0, 1)}
                  </span>
                  <div>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <h3 className="text-sm font-semibold text-foreground-950">{member.name}</h3>
                      <span className="text-xs text-foreground-500">{member.role}</span>
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <FeedbackForm />

            <div className="mt-5 rounded-lg border border-background-200/80 bg-background-100/60 p-5">
              <h2 className="font-heading text-base font-semibold text-foreground-950">直接联系</h2>
              <ul className="mt-3 space-y-3 text-sm">
                <li className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-mail-line" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs text-foreground-500">资料勘误邮箱</span>
                    <a
                      href="mailto:archive@sejishikong.film"
                      className="block truncate text-foreground-900 hover:text-primary-700"
                    >
                      archive@sejishikong.film
                    </a>
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-time-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">回复时间</span>
                    <span className="text-foreground-900">工作日 3 个工作日内</span>
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-50 text-primary-600">
                    <i className="ri-map-pin-line" />
                  </span>
                  <span>
                    <span className="block text-xs text-foreground-500">内容语言</span>
                    <span className="text-foreground-900">简体中文 / 中英片名对照</span>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100/70">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-8 md:py-12">
          <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
            <h2 className="font-heading text-lg font-bold text-foreground-950">使用须知与版权声明</h2>
            <ol className="mt-3 space-y-2.5 text-sm leading-[1.9] text-foreground-700">
              <li className="flex gap-3">
                <span className="font-label text-xs text-accent-600">01</span>
                站内影视作品的剧情介绍、编辑解读与专题文案均为本站原创整理，引用请注明来源「色即是空 影视资料站」。
              </li>
              <li className="flex gap-3">
                <span className="font-label text-xs text-accent-600">02</span>
                影片名称、海报与剧照的著作权归各自权利人所有，本站仅用于介绍、评论与资料检索等合理使用场景。
              </li>
              <li className="flex gap-3">
                <span className="font-label text-xs text-accent-600">03</span>
                本站不提供任何影片的在线播放、下载或资源跳转服务；如发现侵权内容，请通过上方邮箱联系我们，我们会在核实后处理。
              </li>
              <li className="flex gap-3">
                <span className="font-label text-xs text-accent-600">04</span>
                收藏记录仅保存在你当前使用的浏览器本地，清除浏览器数据后会一并消失，本站不会上传该信息。
              </li>
            </ol>
            <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-background-200 pt-4">
              <Link
                to="/category"
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                继续浏览影视分类
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-700"
              >
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;